import _extends from '@babel/runtime/helpers/esm/extends';
import _defineProperty from '@babel/runtime/helpers/esm/defineProperty';
import React from 'react';
import PropTypes from 'prop-types';
import ReactModal from 'react-modal';
import { convertPropToCssClassName } from '@elliemae/ds-classnames';
import CloseIcon from '@elliemae/ds-icons/Close';
import { DSIconSizes } from '@elliemae/ds-basic/Icon';
import DSButton from '@elliemae/ds-basic/Button';
import { MODAL_TYPE, modalTypes } from './constants.js';
import ModalFeedBack from './v1/ModalFeedBack/ModalFeedBack.js';

/* eslint-disable max-lines */

const sizes = ['xsmall', 'small', 'medium', 'large', 'xlarge'];

const iconSizes = ['xxs', 'xs', 's', 'm', 'l', 'xl', 'xxl'];

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys(Object(source), true).forEach(function (key) { _defineProperty(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }

var DSModal = function DSModal(_ref) {
  var _ref$containerProps = _ref.containerProps,
      containerProps = _ref$containerProps === void 0 ? {} : _ref$containerProps,
      _ref$className = _ref.className,
      className = _ref$className === void 0 ? '' : _ref$className,
      _ref$dataTestId = _ref.dataTestId,
      dataTestId = _ref$dataTestId === void 0 ? 'ds-modal' : _ref$dataTestId,
      _ref$additionalFooter = _ref.additionalFooterCssClass,
      additionalFooterCssClass = _ref$additionalFooter === void 0 ? '' : _ref$additionalFooter,
      _ref$style = _ref.style,
      style = _ref$style === void 0 ? {} : _ref$style,
      _ref$iconCloseSize = _ref.iconCloseSize,
      iconCloseSize = _ref$iconCloseSize === void 0 ? DSIconSizes.S : _ref$iconCloseSize,
      _ref$modalType = _ref.modalType,
      modalType = _ref$modalType === void 0 ? MODAL_TYPE.DEFAULT : _ref$modalType,
      _ref$showHeader = _ref.showHeader,
      showHeader = _ref$showHeader === void 0 ? true : _ref$showHeader,
      _ref$showFooter = _ref.showFooter,
      showFooter = _ref$showFooter === void 0 ? true : _ref$showFooter,
      children = _ref.children,
      _ref$centered = _ref.centered,
      centered = _ref$centered === void 0 ? true : _ref$centered,
      _ref$confirmLabel = _ref.confirmLabel,
      confirmLabel = _ref$confirmLabel === void 0 ? 'Accept' : _ref$confirmLabel,
      _ref$modalTitle = _ref.modalTitle,
      modalTitle = _ref$modalTitle === void 0 ? '' : _ref$modalTitle,
      _ref$size = _ref.size,
      size = _ref$size === void 0 ? 'medium' : _ref$size,
      _ref$isOpen = _ref.isOpen,
      isOpen = _ref$isOpen === void 0 ? false : _ref$isOpen,
      _ref$onClose = _ref.onClose,
      onClose = _ref$onClose === void 0 ? function () {
    return null;
  } : _ref$onClose,
      _ref$onAfterOpen = _ref.onAfterOpen,
      onAfterOpen = _ref$onAfterOpen === void 0 ? function () {
    return null;
  } : _ref$onAfterOpen,
      _ref$onConfirm = _ref.onConfirm,
      onConfirm = _ref$onConfirm === void 0 ? function () {
    return null;
  } : _ref$onConfirm,
      _ref$onReject = _ref.onReject,
      onReject = _ref$onReject === void 0 ? function () {
    return null;
  } : _ref$onReject,
      _ref$rejectLabel = _ref.rejectLabel,
      rejectLabel = _ref$rejectLabel === void 0 ? 'Cancel' : _ref$rejectLabel,
      _ref$shouldCloseOnOve = _ref.shouldCloseOnOverlayClick,
      shouldCloseOnOverlayClick = _ref$shouldCloseOnOve === void 0 ? false : _ref$shouldCloseOnOve,
      _ref$hasError = _ref.hasError,
      hasError = _ref$hasError === void 0 ? false : _ref$hasError,
      _ref$hasWarning = _ref.hasWarning,
      hasWarning = _ref$hasWarning === void 0 ? false : _ref$hasWarning,
      _ref$hasSuccess = _ref.hasSuccess,
      hasSuccess = _ref$hasSuccess === void 0 ? false : _ref$hasSuccess,
      _ref$hasInfo = _ref.hasInfo,
      hasInfo = _ref$hasInfo === void 0 ? false : _ref$hasInfo,
      _ref$hasHelp = _ref.hasHelp,
      hasHelp = _ref$hasHelp === void 0 ? false : _ref$hasHelp,
      _ref$actionsRef = _ref.actionsRef,
      actionsRef = _ref$actionsRef === void 0 ? function () {
    return null;
  } : _ref$actionsRef,
      _ref$appElement = _ref.appElement,
      appElement = _ref$appElement === void 0 ? '#root' : _ref$appElement,
      _ref$overridePropsCon = _ref.overridePropsConfirmButton,
      overridePropsConfirmButton = _ref$overridePropsCon === void 0 ? {} : _ref$overridePropsCon,
      _ref$overridePropsRej = _ref.overridePropsRejectButton,
      overridePropsRejectButton = _ref$overridePropsRej === void 0 ? {} : _ref$overridePropsRej,
      _ref$zIndex = _ref.zIndex,
      zIndex = _ref$zIndex === void 0 ? 10 : _ref$zIndex;
  // http://reactcommunity.org/react-modal/accessibility/ https://github.com/reactjs/react-modal#examples
  typeof document !== 'undefined' && document && ReactModal.setAppElement && ReactModal.setAppElement(appElement); // eslint-disable-line

  var _convertPropToCssClas = convertPropToCssClassName('modal', className, {
    hasError: hasError,
    hasWarning: hasWarning,
    hasSuccess: hasSuccess,
    modalType: modalType,
    size: size
  }),
      cssClassName = _convertPropToCssClas.cssClassName,
      classNameElement = _convertPropToCssClas.classNameElement,
      classNameBlock = _convertPropToCssClas.classNameBlock,
      classNameModifier = _convertPropToCssClas.classNameModifier,
      classNameBlockModifier = _convertPropToCssClas.classNameBlockModifier;

  var WARNING = MODAL_TYPE.WARNING,
      ERROR = MODAL_TYPE.ERROR,
      SUCCESS = MODAL_TYPE.SUCCESS,
      HELP = MODAL_TYPE.HELP,
      INFO = MODAL_TYPE.INFO;
  var isAFeedbackType = [WARNING, ERROR, SUCCESS, HELP, INFO].indexOf(modalType) > -1;
  var showFeedBack = isAFeedbackType || hasError || hasWarning || hasSuccess || hasInfo || hasHelp;
  return /*#__PURE__*/React.createElement(ReactModal, {
    testId: dataTestId,
    className: cssClassName,
    contentLabel: modalTitle,
    isOpen: isOpen,
    onAfterOpen: onAfterOpen,
    onRequestClose: onClose,
    overlayClassName: "".concat(classNameBlock('overlay'), " ").concat(classNameModifier(centered && 'center')),
    shouldCloseOnOverlayClick: shouldCloseOnOverlayClick,
    style: {
      content: _objectSpread({}, style),
      overlay: {
        zIndex: zIndex
      }
    }
  }, /*#__PURE__*/React.createElement("div", containerProps, showHeader && /*#__PURE__*/React.createElement("div", {
    className: classNameBlock('modal-header')
  }, /*#__PURE__*/React.createElement("div", {
    className: classNameElement('title', 'modal-header')
  }, modalTitle), /*#__PURE__*/React.createElement("div", {
    className: classNameElement('icon', 'modal-header')
  }, /*#__PURE__*/React.createElement(DSButton, {
    buttonType: "link",
    icon: /*#__PURE__*/React.createElement(CloseIcon, null),
    onClick: onClose,
    size: iconCloseSize
  }))), /*#__PURE__*/React.createElement("div", {
    className: classNameElement('body')
  }, showFeedBack && /*#__PURE__*/React.createElement(ModalFeedBack, {
    hasError: hasError,
    hasHelp: hasHelp,
    hasInfo: hasInfo,
    hasSuccess: hasSuccess,
    hasWarning: hasWarning,
    modalType: modalType
  }), children), showFooter && !isAFeedbackType && /*#__PURE__*/React.createElement("div", {
    className: "".concat(classNameBlock('modal-footer'), " ").concat(additionalFooterCssClass ? classNameBlockModifier(additionalFooterCssClass, 'modal-footer') : '')
  }, /*#__PURE__*/React.createElement("div", {
    ref: actionsRef,
    className: classNameBlock('modal-footer-actions')
  }, modalType === MODAL_TYPE.CONFIRM && /*#__PURE__*/React.createElement(DSButton, _extends({
    buttonType: "secondary",
    className: "".concat(classNameElement('button-action'), " ").concat(classNameModifier('reject', 'button-action'), " "),
    labelText: rejectLabel,
    onClick: onReject
  }, overridePropsRejectButton)), /*#__PURE__*/React.createElement(DSButton, _extends({
    buttonType: "primary",
    className: "".concat(classNameElement('button-action'), " ").concat(classNameModifier('confirm', 'button-action'), " "),
    labelText: confirmLabel,
    onClick: onConfirm
  }, overridePropsConfirmButton))))));
};

DSModal.propTypes = {
  className: PropTypes.string,

  /**
   * Add an additional css class to the footer
   */
  additionalFooterCssClass: PropTypes.string,

  /**
   * Set style for the modal
   */
  style: PropTypes.shape({}),

  /**
   * Select size for the close icon
   */
  iconCloseSize: PropTypes.oneOf(iconSizes),

  /**
   * ['default', 'alert', 'confirm', 'notification', 'warning', 'error', 'success', 'help', 'info']
   */
  modalType: PropTypes.oneOf(modalTypes),

  /**
   * Show the header or not
   */
  showHeader: PropTypes.bool,

  /**
   * Show the footer or not
   */
  showFooter: PropTypes.bool,

  /**
   * Main content of the modal
   */
  children: PropTypes.oneOfType([PropTypes.element, PropTypes.string, PropTypes.any]).isRequired,

  /**
   * Content is centered or not
   */
  centered: PropTypes.bool,

  /**
   * Text to be added in the confirm button
   */
  confirmLabel: PropTypes.string,

  /**
   * Text to be added in the modal title
   */
  modalTitle: PropTypes.string,

  /**
   * Text to be added in the modal title
   */
  size: PropTypes.oneOf(sizes),

  /**
   * Modal is open or not
   */
  isOpen: PropTypes.bool,

  /**
   * Allows a function to be triggered once the modal is closed
   */
  onClose: PropTypes.func,

  /**
   * Allows a function to be triggered once the modal is opened
   */
  onAfterOpen: PropTypes.func,

  /**
   * Allows a function to be triggered once the modal open is confirmed
   */
  onConfirm: PropTypes.func,

  /**
   * Allows a function to be triggered once the modal open is rejected
   */
  onReject: PropTypes.func,

  /**
   * Text to be added in the reject button
   */
  rejectLabel: PropTypes.string,

  /**
   * If modal should close on overlay click
   */
  shouldCloseOnOverlayClick: PropTypes.bool,

  /**
   * Modal has error or not
   */
  hasError: PropTypes.bool,

  /**
   * Modal has warning or not
   */
  hasWarning: PropTypes.bool,

  /**
   * Modal has success or not
   */
  hasSuccess: PropTypes.bool,

  /**
   * Modal has info or not
   */
  hasInfo: PropTypes.bool,

  /**
   * Modal has help or not
   */
  hasHelp: PropTypes.bool,
  actionsRef: PropTypes.func,
  appElement: PropTypes.string,

  /**
   * Customized props for the confirm button
   */
  overridePropsConfirmButton: PropTypes.shape({}),

  /**
   * Customized props for the reject button
   */
  overridePropsRejectButton: PropTypes.shape({})
};

export { DSModal as D, iconSizes as i, sizes as s };
//# sourceMappingURL=DSModal-301a5489.js.map
